// =============================================
// Collapsible page menu (Masters / Transactions / Reports / Utilities + its sub-tabs)
// =============================================
// The menu bar and sub-tab row of a pillar page take two rows of every screen. The arrow
// at the bottom right folds them away so the page below gets the room; the breadcrumb row
// stays, and carries the arrow that brings the menu back.
//
// The pin decides how long that lasts:
//   not pinned — folded only for the page you are on; the menu is back on the next page
//   pinned     — the menu stays as it is now (folded or open) on every page and next time
//
// One preference for all pillars, kept per browser (a display preference, not data).
// =============================================
import React, { useCallback, useEffect, useState } from 'react';
import { FiChevronUp, FiChevronDown } from 'react-icons/fi';
import { LuPin, LuPinOff } from 'react-icons/lu';

const STORAGE_KEY = 'akt_menu_collapse';

interface MenuCollapseState { collapsed: boolean; pinned: boolean }

function readSaved(): MenuCollapseState {
  try {
    const saved = JSON.parse(localStorage.getItem(STORAGE_KEY) || 'null');
    if (saved && saved.pinned) return { collapsed: !!saved.collapsed, pinned: true };
  } catch { /* no saved preference */ }
  return { collapsed: false, pinned: false };
}

function save(state: MenuCollapseState): void {
  try {
    if (state.pinned) localStorage.setItem(STORAGE_KEY, JSON.stringify(state));
    else localStorage.removeItem(STORAGE_KEY);
  } catch { /* preference simply not kept */ }
}

export interface MenuCollapse extends MenuCollapseState {
  toggleCollapsed: () => void;
  togglePinned: () => void;
}

/** `pageKey` = the page being shown; an unpinned fold ends when it changes. */
export function useMenuCollapse(pageKey: string | null): MenuCollapse {
  const [state, setState] = useState<MenuCollapseState>(readSaved);

  useEffect(() => {
    setState(s => (s.pinned || !s.collapsed ? s : { ...s, collapsed: false }));
  }, [pageKey]);

  const toggleCollapsed = useCallback(() => {
    setState(s => { const next = { ...s, collapsed: !s.collapsed }; save(next); return next; });
  }, []);
  const togglePinned = useCallback(() => {
    setState(s => { const next = { ...s, pinned: !s.pinned }; save(next); return next; });
  }, []);

  return { ...state, toggleCollapsed, togglePinned };
}

/** The arrow + pin. `className` places it (end of the sub-tab row / breadcrumb row). */
export const MenuCollapseControls: React.FC<{ menu: MenuCollapse; className?: string }> = ({ menu, className = '' }) => (
  <span className={`bk-menu-collapse ${className}`}>
    <button
      type="button"
      className={`bk-menu-collapse-btn ${menu.pinned ? 'on' : ''}`}
      onClick={menu.togglePinned}
      aria-pressed={menu.pinned}
      title={menu.pinned
        ? `Pinned — the menu stays ${menu.collapsed ? 'hidden' : 'shown'} on every page. Click to unpin.`
        : `Pin — keep the menu ${menu.collapsed ? 'hidden' : 'shown'} on every page`}
    >
      {menu.pinned ? <LuPin size={14} /> : <LuPinOff size={14} />}
    </button>
    <button
      type="button"
      className="bk-menu-collapse-btn"
      onClick={menu.toggleCollapsed}
      aria-expanded={!menu.collapsed}
      title={menu.collapsed ? 'Show the menu' : 'Hide the menu for more room'}
    >
      {menu.collapsed ? <FiChevronDown size={16} /> : <FiChevronUp size={16} />}
    </button>
  </span>
);
